import React, { memo, useContext, useMemo, useCallback } from 'react';
import useI18n from '../../../../i18n/hooks/useI18n';
import DetailsCard from './DetailsCard';
import { SELLER_TAKE_RATE } from '../../constants';
import TransactionContext from '../contexts/TransactionContext';
import S from './style/PaymentCard';
import { Transaction, PriceComponent } from '../../../../../typings/transaction';
import PriceLine from './PriceLine';
import InfoIconSTR from './InfoIconSTR';
import { TrackEvent } from '../../../ad/components/types/index';
import trackingKeys from '../../../../commons/constants/trackingKeys';

interface PaymentCardProps {
  desktop: boolean;
  trackEvent: TrackEvent;
}

type CodeComponentMap = Record<PriceComponent['code'], PriceComponent>;

const PaymentCard: React.FC<PaymentCardProps> = ({ desktop, trackEvent }) => {
  const { ad, order, paymentMethod } = useContext(TransactionContext) || {};
  const i18n = useI18n();

  const infoContent = i18n`dlv_orders_details_seller_tr_info`;

  const i18nKey = useCallback(
    (key: string | TemplateStringsArray) => i18n(`dlv_orders_details_${key}`),
    [i18n],
  );

  const paymentTypeI18n: Record<Transaction['paymentMethod'], string> = useMemo(
    () => ({
      cod: i18nKey`cash_on_delivery`,
      marketpay: i18nKey('online_payment'),
    }),
    [i18nKey],
  );

  const priceComponents: CodeComponentMap = useMemo(
    () =>
      order?.price?.components?.reduce((acc: CodeComponentMap, comp) => {
        acc[comp.code] = comp;
        return acc;
      }, {}),
    [order],
  );

  const lines =
    Object.values(priceComponents) ||
    ['line-1', 'line-2'].map(code => ({ code } as PriceComponent));

  const extraTrackingParams = {
    ad_id: ad.id,
    touch_point_page: trackingKeys.touchPointPage.deliveryOverview,
  };

  const tooltip = (
    <InfoIconSTR
      desktop={desktop}
      infoContent={infoContent}
      extraTrackingParams={extraTrackingParams}
      trackEvent={trackEvent}
    />
  );

  return (
    <DetailsCard
      data-cy="payment-info"
      title={i18nKey`paymentmethod`}
      subtitle={paymentTypeI18n?.[paymentMethod] || null}
    >
      <S.PriceBreakBlock>
        {lines.length > 1 &&
          lines.map(({ label, value, code }) => (
            <PriceLine
              key={code}
              code={code}
              value={value}
              label={label}
              desktop={desktop}
              tooltip={code === SELLER_TAKE_RATE ? tooltip : null}
            />
          ))}
        <PriceLine
          bold
          code="total"
          value={order?.price?.total}
          title={order?.price?.total?.label?.pl}
        />
      </S.PriceBreakBlock>
    </DetailsCard>
  );
};

export default memo(PaymentCard);
